import { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../styles/service.css";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const Services = () => {
  const [activeTab, setActiveTab] = useState("Women");
  const [expandedCategory, setExpandedCategory] = useState(null);
  const navigate = useNavigate();
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero lines
      gsap.from(".sv-hero-line", {
        yPercent: 110,
        opacity: 0,
        duration: 1.3,
        stagger: 0.16,
        ease: "expo.out",
        delay: 0.2,
      });
      gsap.from(".sv-hero-sub", {
        opacity: 0,
        y: 22,
        duration: 1,
        delay: 0.9,
        ease: "power3.out",
      });
      gsap.from(".sv-hero-cta", {
        opacity: 0,
        y: 18,
        duration: 1,
        delay: 1.1,
        ease: "power3.out",
      });

      // Reveal-up elements
      gsap.utils.toArray(".sv-reveal").forEach((el) => {
        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: "top 88%",
            toggleActions: "play none none none",
          },
          y: 50,
          opacity: 0,
          duration: 0.9,
          ease: "power3.out",
        });
      });

      // Stagger category cards
      ScrollTrigger.create({
        trigger: ".sv-categories-grid",
        start: "top 85%",
        onEnter: () => {
          gsap.from(".sv-cat-card", {
            y: 60,
            opacity: 0,
            duration: 0.7,
            stagger: 0.08,
            ease: "power2.out",
          });
        },
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  // Re-animate on tab change
  useEffect(() => {
    gsap.fromTo(
      ".sv-cat-card",
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.5, stagger: 0.06, ease: "power2.out" }
    );
  }, [activeTab]);

  const menuData = {
    Women: [
      {
        category: "Hair Cut",
        icon: "✂",
        image: "/assets/serviceimages/Haircut.jpeg",
        services: [
          { name: "Front Flick", price: "200" },
          { name: "Boy Cut", price: "400" },
          { name: "Blunt & Bob", price: "500" },
          { name: "Straight & U Cut", price: "550" },
          { name: "Layer Cut", price: "600" },
        ],
      },
      {
        category: "Threading",
        icon: "〰",
        image: "/assets/serviceimages/threadin.jpeg",
        services: [
          { name: "Forehead", price: "10" },
          { name: "Upper Lip", price: "10" },
          { name: "Chin", price: "10" },
          { name: "Side Locks", price: "40" },
          { name: "Eye Brow", price: "50" },
          { name: "Full Face", price: "150" },
        ],
      },
      {
        category: "Waxing",
        icon: "◈",
        image: "/assets/serviceimages/waxin.jpeg",
        isTwoPrice: true,
        label1: "Regular",
        label2: "Premium",
        services: [
          { name: "Chin", price: "30", price1: "10" },
          { name: "Upper Lip", price: "30", price1: "10" },
          { name: "For Head", price: "60", price1: "10" },
          { name: "Side Lock", price: "100", price1: "80" },
          { name: "Under Arms", price: "200", price1: "150" },
          { name: "Full Face", price: "250", price1: "200" },
          { name: "Half Arms", price: "400", price1: "300" },
          { name: "Full Arms", price: "500", price1: "400" },
          { name: "Half Leg", price: "600", price1: "500" },
          { name: "Full Leg", price: "1300", price1: "1100" },
          { name: "Full Body", price: "5000", price1: "3000" },
        ],
      },
      {
        category: "Hair Color",
        icon: "◎",
        image: "/assets/serviceimages/Haircolo.jpeg",
        services: [
          { name: "Garnier", price: "1000" },
          { name: "Matrix", price: "1500" },
          { name: "Loreal", price: "2000" },
          { name: "Schwarzkope", price: "1600" },
          { name: "Speedy", price: "1800" },
          { name: "Amonia Free", price: "2000" },
          { name: "Top Speedy", price: "2000" },
        ],
      },
      {
        category: "Facial",
        icon: "✦",
        image: "/assets/serviceimages/Facia.jpeg",
        services: [
          { name: "Basic Facial", price: "700" },
          { name: "Fruit Facial", price: "1000" },
          { name: "Aroma Facial", price: "1200" },
          { name: "Anti Ageing", price: "1500" },
          { name: "Silver Facial", price: "1600" },
          { name: "Ordinary Gold", price: "1700" },
          { name: "Ordinary Pearl", price: "1800" },
          { name: "Tan Removal", price: "1800" },
          { name: "Skin Whitening", price: "1900" },
          { name: "Oxy Vitalife", price: "2000" },
          { name: "Diamond", price: "2100" },
          { name: "Hydra Facial", price: "3700" },
        ],
      },
      {
        category: "Bleach & D-Tan",
        icon: "◇",
        image: "/assets/serviceimages/dta.jpeg",
        isBleach: true,
        services: [
          { name: "Under Arms", oxy: "150", fruit: "200", gold: "300", dtan: "400" },
          { name: "Neck", oxy: "200", fruit: "300", gold: "400", dtan: "500" },
          { name: "Face", oxy: "350", fruit: "400", gold: "500", dtan: "600" },
          { name: "Foot", oxy: "400", fruit: "500", gold: "600", dtan: "700" },
          { name: "Half Hand", oxy: "500", fruit: "600", gold: "700", dtan: "800" },
          { name: "Half Leg", oxy: "600", fruit: "700", gold: "800", dtan: "900" },
          { name: "Full Hand", oxy: "900", fruit: "1000", gold: "1100", dtan: "1200" },
          { name: "Full Leg", oxy: "1300", fruit: "1400", gold: "1500", dtan: "1600" },
          { name: "Full Body", oxy: "4500", fruit: "5000", gold: "5500", dtan: "6000" },
        ],
      },
      {
        category: "Hair Highlighting",
        icon: "✧",
        image: "/assets/serviceimages/hairhigh.jpeg",
        services: [
          { name: "One Strip", price: "300" },
          { name: "Highlight", price: "1500" },
          { name: "Global Colour", price: "2000" },
        ],
      },
      {
        category: "Body",
        icon: "◉",
        image: "/assets/serviceimages/Wbod.jpeg",
        services: [
          { name: "Ear Lobe Piercing" },
          { name: "Wart Removal" },
          { name: "Tattoos" },
          { name: "Body Piercing" },
        ],
      },
      {
        category: "Massage",
        icon: "⊕",
        image: "/assets/serviceimages/wfacems.jpeg",
        services: [
          { name: "Face", price: "350" },
          { name: "Head", price: "400" },
        ],
      },
      {
        category: "Cleanup",
        icon: "❋",
        image: "/assets/serviceimages/Hairclea.jpeg",
        services: [
          { name: "Normal", price: "450" },
          { name: "Fruit", price: "550" },
          { name: "Aroma", price: "700" },
          { name: "Silver", price: "800" },
          { name: "Gold", price: "900" },
          { name: "Pearl", price: "1100" },
          { name: "Diamond", price: "1300" },
          { name: "Hydra Cleanup", price: "1800" },
        ],
      },
      {
        category: "Hair Spa",
        icon: "◌",
        image: "/assets/serviceimages/whairsp.jpeg",
        isSpa: true,
        services: [
          { name: "Normal Spa", small: "1000", large: "1200" },
          { name: "Black Spa", small: "1100", large: "1300" },
          { name: "Repair", small: "1000", large: "1200" },
          { name: "Colour Care", small: "1000", large: "1200" },
          { name: "Smoothening", small: "1100", large: "1300" },
          { name: "Dandruff", small: "1300", large: "1500" },
          { name: "Keratin", small: "1600", large: "1800" },
          { name: "Nano Plasta", small: "1900", large: "2100" },
        ],
      },
      {
        category: "Shahanaz",
        icon: "⊛",
        image: "/assets/serviceimages/shahanaz.jpeg",
        services: [
          { name: "Veg Peel", price: "1000" },
          { name: "Shahanaz Normal", price: "1700" },
          { name: "Shahanaz Pearl", price: "2500" },
          { name: "Shahanaz Gold", price: "2800" },
          { name: "Shahanaz Diamond", price: "3000" },
        ],
      },
      {
        category: "Henna",
        icon: "❀",
        image: "/assets/serviceimages/Henn.jpeg",
        services: [
          { name: "Natural Henna", price: "800" },
          { name: "Colour Henna", price: "750" },
          { name: "Black Henna", price: "700" },
        ],
      },
      {
        category: "Manicure",
        icon: "◈",
        image: "/assets/serviceimages/manicure.jpeg",
        services: [
          { name: "Normal", price: "600" },
          { name: "Spa Manicure", price: "800" },
          { name: "Aroma", price: "1000" },
          { name: "Bloom Manicure", price: "1200" },
          { name: "K&Q SPL", price: "1500" },
        ],
      },
      {
        category: "Pedicure",
        icon: "◈",
        image: "/assets/serviceimages/pedicur.jpeg",
        services: [
          { name: "Normal", price: "1000" },
          { name: "Spa Pedicure", price: "1500" },
          { name: "Aroma", price: "1700" },
          { name: "Bloom Pedicure", price: "1900" },
          { name: "K&Q SPL", price: "2100" },
        ],
      },
      {
        category: "Hair Treatment",
        icon: "✦",
        image: "/assets/serviceimages/hairtreatmen.jpeg",
        services: [
          { name: "Blow Dry", price: "450" },
          { name: "Ironing", price: "800" },
          { name: "Rebonding", price: "3000" },
          { name: "Smoothening", price: "5000" },
          { name: "Straightening", price: "4500" },
          { name: "Permanent Blow Dry", price: "5000" },
          { name: "Keratin", price: "6000" },
          { name: "Nano Plasta", price: "7000" },
        ],
      },
      {
        category: "K&Q Special",
        icon: "★",
        image: "/assets/serviceimages/h.jpeg",
        services: [
          { name: "Hydra Facial", price: "On Consultation" },
        ],
      },
    ],

    Men: [
      {
        category: "Hair Cut",
        icon: "✂",
        image: "/assets/serviceimages/MHaircu.jpeg",
        services: [
          { name: "Kids", price: "150" },
          { name: "Gents", price: "180" },
          { name: "Beard Trimming", price: "150" },
          { name: "Shaving", price: "120" },
          { name: "Change Of Hairstyle", price: "300" },
        ],
      },
      {
        category: "Henna",
        icon: "❀",
        image: "/assets/serviceimages/menmehendi.webp",
        services: [
          { name: "Black", price: "400" },
          { name: "Colour Henna", price: "450" },
          { name: "Natural Henna", price: "500" },
        ],
      },
      {
        category: "Clean Up",
        icon: "❋",
        image: "/assets/serviceimages/mHairwas.jpeg",
        services: [
          { name: "Normal", price: "450" },
          { name: "Fruit", price: "550" },
          { name: "Aroma", price: "650" },
          { name: "Silver", price: "750" },
          { name: "Gold", price: "850" },
        ],
      },
      {
        category: "Massage",
        icon: "⊕",
        image: "/assets/serviceimages/menkqspl.jpeg",
        services: [
          { name: "Face", price: "350" },
          { name: "Head", price: "350" },
        ],
      },
      {
        category: "Facial",
        icon: "✦",
        image: "/assets/serviceimages/Mfacia.jpeg",
        services: [
          { name: "Basic Facial", price: "800" },
          { name: "Fruit Facial", price: "1000" },
          { name: "Aroma Facial", price: "1200" },
          { name: "Anti Ageing", price: "1400" },
          { name: "Silver Facial", price: "1500" },
          { name: "Ordinary Gold", price: "1600" },
          { name: "Ordinary Pearl", price: "1700" },
          { name: "Tan Removal", price: "1700" },
          { name: "Skin Whitening", price: "1800" },
          { name: "Oxy Vitalife", price: "1900" },
          { name: "Diamond", price: "2000" },
          { name: "Hydra Facial", price: "3500" },
        ],
      },
      {
        category: "Hair Spa",
        icon: "◌",
        image: "/assets/serviceimages/mhairsp.jpeg",
        services: [
          { name: "Normal Spa", price: "1000" },
          { name: "Black Spa", price: "1100" },
          { name: "Repair", price: "1000" },
          { name: "Colour Care", price: "1000" },
          { name: "Smoothening", price: "1100" },
          { name: "Dandruff", price: "1300" },
          { name: "Keratin", price: "1600" },
          { name: "Nano Plasta", price: "1900" },
        ],
      },
      {
        category: "Bleach & D-Tan",
        icon: "◇",
        image: "/assets/serviceimages/Mbleac.jpeg",
        isBleach: true,
        services: [
          { name: "Face", oxy: "400", fruit: "450", gold: "550", dtan: "650" },
          { name: "Foot", oxy: "450", fruit: "550", gold: "650", dtan: "750" },
          { name: "Half Hand", oxy: "550", fruit: "650", gold: "750", dtan: "850" },
          { name: "Half Leg", oxy: "650", fruit: "750", gold: "850", dtan: "950" },
          { name: "Full Hand", oxy: "950", fruit: "1100", gold: "1100", dtan: "1200" },
        ],
      },
      {
        category: "Shahanaz",
        icon: "⊛",
        image: "/assets/serviceimages/groomtouchup.jpeg",
        services: [
          { name: "Veg Peel", price: "900" },
          { name: "Shahanaz Normal", price: "1600" },
          { name: "Shahanaz Pearl", price: "2400" },
          { name: "Shahanaz Gold", price: "2700" },
          { name: "Shahanaz Diamond", price: "2900" },
        ],
      },
      {
        category: "Body",
        icon: "◉",
        image: "/assets/serviceimages/mBod.jpeg",
        services: [
          { name: "Ear Lobe Piercing" },
          { name: "Wart Removal" },
          { name: "Tattoos" },
          { name: "Body Piercing" },
        ],
      },
      {
        category: "Manicure",
        icon: "◈",
        image: "/assets/serviceimages/Menmanicur.jpeg",
        services: [
          { name: "Normal", price: "500" },
          { name: "Spa Manicure", price: "700" },
          { name: "Aroma", price: "900" },
          { name: "Bloom Manicure", price: "1100" },
        ],
      },
      {
        category: "Pedicure",
        icon: "◈",
        image: "/assets/serviceimages/Mpedicur.jpeg",
        services: [
          { name: "Normal", price: "900" },
          { name: "Spa Pedicure", price: "1400" },
          { name: "Aroma", price: "1600" },
          { name: "Bloom Pedicure", price: "1800" },
        ],
      },
      {
        category: "Hair Treatment",
        icon: "✦",
        image: "/assets/serviceimages/hairtrt.jpeg",
        services: [
          { name: "Blow Dry", price: "200" },
          { name: "Ironing", price: "400" },
          { name: "Rebonding", price: "1600" },
          { name: "Smoothening", price: "1800" },
          { name: "Straightening", price: "1900" },
          { name: "Permanent Blow Dry", price: "2200" },
          { name: "Keratin", price: "2500" },
          { name: "Nano Plasta", price: "2800" },
          { name: "Micro Bonding", price: "3000" },
        ],
      },
      {
        category: "Hair Highlighting",
        icon: "✧",
        image: "/assets/serviceimages/menhairhighlight.jpeg",
        services: [
          { name: "Garnier", price: "450" },
          { name: "Matrix", price: "600" },
          { name: "Loreal", price: "650" },
          { name: "Schwarzkopp", price: "650" },
          { name: "Speedy", price: "750" },
          { name: "Amonia Free", price: "850" },
        ],
      },
      {
        category: "Groom Touch Up",
        icon: "◎",
        image: "/assets/serviceimages/groomtouchup.jpeg",
        services: [
          { name: "Party", price: "1600" },
          { name: "Shaving", price: "1000" },
        ],
      },
      {
        category: "K&Q Special",
        icon: "★",
        image: "/assets/serviceimages/mhydr.jpeg",
        services: [
          { name: "Hydra Facial", price: "3500" },
        ],
      },
    ],
  };

  const toggleCategory = (idx) => {
    setExpandedCategory(expandedCategory === idx ? null : idx);
  };

  return (
    <div className="sv-root" ref={pageRef}>

      {/* ══════════════════════ HERO ══════════════════════ */}
      <section className="sv-hero">
        <div className="sv-hero-bg">
          <img src="/assets/service/serbg.jpg" alt="services" className="sv-hero-img" />
          <div className="sv-hero-overlay" />
        </div>
        <div className="sv-hero-content">
          <p className="sv-hero-eyebrow sv-hero-sub">King & Queen · Services</p>
          <div className="sv-hero-title">
            <div className="sv-clip"><span className="sv-hero-line">Premium</span></div>
            <div className="sv-clip"><span className="sv-hero-line sv-italic">Beauty Services</span></div>
          </div>
          <p className="sv-hero-tagline sv-hero-sub">
            Professional care, premium products, and personalized treatments — all under one roof.
          </p>
          <div className="sv-hero-cta">
            <button className="sv-btn-primary" onClick={() => navigate("/contact")}>
              Book an Appointment
            </button>
            <button className="sv-btn-ghost" onClick={() => navigate("/about")}>
              About Us
            </button>
          </div>
        </div>
        <div className="sv-hero-scroll">
          <span>Scroll</span>
          <div className="sv-scroll-bar" />
        </div>
      </section>

      {/* ══════════════════════ INTRO STATEMENT ══════════════════════ */}
      <section className="sv-statement">
        <div className="sv-statement-inner sv-reveal">
          <p className="sv-statement-text">
            "From precision cuts to transformative treatments — every service we offer
            is crafted with expertise, premium products, and an unwavering dedication
            to your confidence."
          </p>
          <span className="sv-statement-attr">— King &amp; Queen Studio</span>
        </div>
      </section>

      {/* ══════════════════════ MENU SECTION ══════════════════════ */}
      <section className="sv-menu">

        {/* Header */}
        <div className="sv-menu-header sv-reveal">
          <p className="sv-eyebrow">Our Menu</p>
          <h2 className="sv-section-title">
            Premium <em>Services</em>
          </h2>
          <p className="sv-menu-sub">
            Select a category below to explore our full range of treatments.
          </p>
        </div>

        {/* Gender Tabs */}
        <div className="sv-tabs sv-reveal">
          {Object.keys(menuData).map((tab) => (
            <button
              key={tab}
              className={`sv-tab ${activeTab === tab ? "sv-tab-active" : ""}`}
              onClick={() => { setActiveTab(tab); setExpandedCategory(null); }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Category Cards Grid */}
        <div className="sv-categories-grid">
          {menuData[activeTab].map((cat, idx) => {
            const isOpen = expandedCategory === idx;
            return (
              <div
                key={idx}
                className={`sv-cat-card ${isOpen ? "sv-cat-open" : ""}`}
              >
                {/* Card Header — always visible */}
                <div className="sv-cat-header" onClick={() => toggleCategory(idx)}>
                  <div className="sv-cat-image-wrap">
                    <img src={cat.image} alt={cat.category} />
                    <div className="sv-cat-img-overlay" />
                  </div>
                  <div className="sv-cat-title-row">
                    <div className="sv-cat-title-left">
                      <span className="sv-cat-icon">{cat.icon}</span>
                      <span className="sv-cat-name">{cat.category}</span>
                    </div>
                    <span className="sv-cat-count">{cat.services.length} services</span>
                    <span className={`sv-cat-arrow ${isOpen ? "sv-open" : ""}`}>↓</span>
                  </div>
                </div>

                {/* Expanded Services List */}
                <div className={`sv-cat-body ${isOpen ? "sv-body-open" : ""}`}>
                  <div className="sv-cat-body-inner">

                    {/* Bleach & D-Tan special table */}
                    {cat.isBleach ? (
                      <div className="sv-bleach-table">
                        <div className="sv-bleach-head">
                          <span>Service</span>
                          <span>Oxy</span>
                          <span>Fruit</span>
                          <span>Gold</span>
                          <span>D-Tan</span>
                        </div>
                        {cat.services.map((s, i) => (
                          <div key={i} className="sv-bleach-row">
                            <span>{s.name}</span>
                            <span>₹{s.oxy}</span>
                            <span>₹{s.fruit}</span>
                            <span>₹{s.gold}</span>
                            <span>₹{s.dtan}</span>
                          </div>
                        ))}
                      </div>
                    ) : cat.isSpa ? (
                      /* Hair Spa — small/large */
                      <>
                        <div className="sv-spa-head">
                          <span>Service</span>
                          <span>Small</span>
                          <span>Large</span>
                        </div>
                        {cat.services.map((s, i) => (
                          <div key={i} className="sv-service-row">
                            <span className="sv-sname">{s.name}</span>
                            <div className="sv-prices">
                              <span className="sv-price">₹{s.small}</span>
                              <span className="sv-price">₹{s.large}</span>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : cat.isTwoPrice ? (
                      /* Two-price (waxing) */
                      <>
                        <div className="sv-spa-head">
                          <span>Service</span>
                          <span>{cat.label1 || "Price 1"}</span>
                          <span>{cat.label2 || "Price 2"}</span>
                        </div>
                        {cat.services.map((s, i) => (
                          <div key={i} className="sv-service-row">
                            <span className="sv-sname">{s.name}</span>
                            <div className="sv-prices">
                              <span className="sv-price">₹{s.price}</span>
                              {s.price1 && <span className="sv-price">₹{s.price1}</span>}
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      /* Standard single price */
                      cat.services.map((s, i) => (
                        <div key={i} className="sv-service-row">
                          <span className="sv-sname">{s.name}</span>
                          <div className="sv-prices">
                            {s.price ? (
                              <span className="sv-price">
                                {isNaN(s.price) ? s.price : `₹${s.price}`}
                              </span>
                            ) : (
                              <span className="sv-price-consult">On Consult</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}

                    <button
                      className="sv-book-btn"
                      onClick={() => navigate("/contact")}
                    >
                      Book {cat.category} →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ══════════════════════ CTA STRIP ══════════════════════ */}
      <section className="sv-cta">
        <div className="sv-cta-inner sv-reveal">
          <p className="sv-eyebrow" style={{ color: "var(--sv-gold)" }}>Ready to Transform?</p>
          <h2 className="sv-section-title" style={{ color: "#fff" }}>
            Book your<br /><em>appointment today</em>
          </h2>
          <button className="sv-btn-gold" onClick={() => navigate("/contact")}>
            Reserve a Slot
          </button>
        </div>
        <div className="sv-cta-images">
          <img src="/assets/aboutimg/Product6.webp" alt="hair" />
          <img src="/assets/aboutimg/Model7.webp" alt="bridal" className="sv-cta-img2" />
        </div>
      </section>
    </div>
  );
};

export default Services;